<template>
    <v-main class="d-flex align-center justify-center" style="height: 100vh">
        <v-card max-width="440" class="pa-6" elevation="2">
            <div class="text-center mb-6">
                <v-icon size="64" color="primary">mdi-flask-outline</v-icon>
                <h1 class="text-h5 mt-4">PerformX UAT Apps</h1>
                <p class="text-body-2 text-medium-emphasis mt-2">Choose a tenant to work in</p>
            </div>

            <v-alert v-if="session.error.value" type="error" variant="tonal" class="mb-4" closable>
                {{ errorMessage }}
            </v-alert>

            <v-alert
                v-if="tenantError"
                type="error"
                variant="tonal"
                class="mb-4"
                closable
                data-testid="tenant-error"
                @click:close="tenantError = ''"
            >
                {{ tenantError }}
            </v-alert>

            <v-alert v-if="hostTenant" type="info" variant="tonal" class="mb-4" density="compact">
                Preselected <strong>{{ hostTenant.name }}</strong> from this address. You can still
                pick another.
            </v-alert>

            <v-radio-group v-model="selectedTenantId" hide-details class="mb-2">
                <v-radio
                    v-for="tenant in TENANT_OPTIONS"
                    :key="tenant.tenantId"
                    :value="tenant.tenantId"
                    :label="tenant.name"
                />
            </v-radio-group>

            <v-btn
                color="primary"
                variant="flat"
                block
                size="large"
                :disabled="!selectedTenantId"
                :loading="switching"
                data-testid="confirm-tenant"
                @click="confirmTenant"
            >
                <v-icon start>mdi-login</v-icon>
                {{ session.isAuthenticated.value ? 'Continue' : 'Sign in with Auth0' }}
            </v-btn>
        </v-card>
    </v-main>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useSession } from '../session/session';
import { TENANT_OPTIONS, resolveTenantFromHostname } from '../session/tenants';

const session = useSession();
const route = useRoute();
const router = useRouter();

// The hostname only ever preselects. Keeping the picker means an unrecognised
// host degrades to the normal flow instead of dead-ending, and a tester can still
// switch tenants without editing the URL.
const hostTenant = resolveTenantFromHostname();

const selectedTenantId = ref<string | null>(session.tenantId.value ?? hostTenant?.tenantId ?? null);
const switching = ref(false);
const tenantError = ref('');

/** Where to land after sign-in. Survives the Auth0 redirect via appState.target. */
const redirectTarget = computed(() => {
    const target = route.query.redirect;
    return typeof target === 'string' && target.startsWith('/') ? target : '/';
});

const errorMessage = computed(() => {
    const error = session.error.value;
    return error instanceof Error ? error.message : String(error ?? '');
});

async function confirmTenant() {
    if (!selectedTenantId.value) return;

    switching.value = true;
    tenantError.value = '';
    try {
        await session.selectTenant(selectedTenantId.value, redirectTarget.value);

        // Only reached when already authenticated — the unauthenticated path
        // leaves for Auth0 and comes back through the plugin's own router.push.
        if (session.isAuthenticated.value) {
            await router.replace(redirectTarget.value);
        }
    } catch (e) {
        console.error('[shell] failed to select tenant:', e);
        tenantError.value = 'Failed to switch tenant. Try signing out and back in.';
    } finally {
        switching.value = false;
    }
}
</script>
